import {
  Box,
  Typography,
  Button,
  Avatar,
  TextField,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
} from '@mui/material';
import {
  CloudUpload,
  Image,
  RestartAlt,
  ExpandMore,
  UnfoldMore,
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { SortableHeaderButton } from '../../../components/TableHeaderButton';
import { getIconSrc } from '../../../utils/getIconSrc';

const arrowStylingDefault = {
  transition: 'transform 150ms ease',
};

const arrowStyling = {
  transform: 'rotate(-180deg)',
  transition: 'transform 150ms ease',
};

interface IconItem {
  name: string;
  key: string;
  category: string;
  type: string;
  editableFields: {
    icon: boolean;
    description: boolean;
    name: boolean;
  };
  current: {
    icon: string;
    description: string;
    name?: string;
    url: string;
  };
}

interface IconsTabDesktopProps {
  filteredAndSortedItems: IconItem[];
  sortBy: 'name' | 'type';
  sortOrder: 'asc' | 'desc';
  onSort: (column: 'name' | 'type') => void;
  editingDescription: string | null;
  editingName: string | null;
  onStartEditingName: (category: string, key: string) => void;
  onFinishEditingName: (category: string, key: string, name: string) => void;
  onCancelEditingName: () => void;
  impersonationLock: boolean;
  resettingItems: Set<string>;
  onStartEditing: (category: string, key: string) => void;
  onFinishEditing: (category: string, key: string, description: string) => void;
  onCancelEditing: () => void;
  onIconChange: (category: string, key: string, file: File) => void;
  onResetIcon: (category: string, key: string) => void;
  getIconFieldError: (
    category: string,
    iconKey: string
  ) => string[] | undefined;
  baseAssetUrl: string;
}

export function IconsTabDesktop({
  filteredAndSortedItems,
  sortBy,
  sortOrder,
  onSort,
  editingDescription,
  editingName,
  onStartEditingName,
  onFinishEditingName,
  onCancelEditingName,
  impersonationLock,
  resettingItems,
  onStartEditing,
  onFinishEditing,
  onCancelEditing,
  onIconChange,
  onResetIcon,
  getIconFieldError,
  baseAssetUrl,
}: IconsTabDesktopProps) {
  const { t } = useTranslation();

  const getArrowIcon = (key: 'name' | 'type') => {
    if (sortBy === key) {
      return sortOrder === 'asc' ? (
        <ExpandMore fontSize="small" style={arrowStyling} />
      ) : (
        <ExpandMore fontSize="small" style={arrowStylingDefault} />
      );
    }
    return <UnfoldMore fontSize="small" style={arrowStylingDefault} />;
  };

  return (
    <Table
      sx={{
        minWidth: 650,
      }}
      aria-label="Icons management"
    >
      <TableHead>
        <TableRow>
          <TableCell
            align="center"
            sx={{
              width: { xs: 60, md: 90 },
            }}
          >
            {t('branding.iconsTab.tableHeaders.icon')}
          </TableCell>
          <TableCell align="center" width="10%">
            {t('branding.iconsTab.tableHeaders.upload')}
          </TableCell>
          <TableCell align="center" width="10%">
            {t('branding.iconsTab.tableHeaders.reset')}
          </TableCell>
          <TableCell align="left" width="15%">
            <SortableHeaderButton
              variant="secondary"
              sx={{ justifyContent: 'left' }}
              onClick={() => onSort('name')}
              isSorted={sortBy === 'name'}
              sortDirection={sortOrder}
            >
              {t('branding.iconsTab.tableHeaders.name')}
              {getArrowIcon('name')}
            </SortableHeaderButton>
          </TableCell>
          <TableCell align="left" width="10%">
            <SortableHeaderButton
              variant="secondary"
              sx={{ justifyContent: 'left' }}
              onClick={() => onSort('type')}
              isSorted={sortBy === 'type'}
              sortDirection={sortOrder}
            >
              {t('branding.iconsTab.tableHeaders.type')}
              {getArrowIcon('type')}
            </SortableHeaderButton>
          </TableCell>
          <TableCell align="left">
            {t('branding.iconsTab.tableHeaders.description')}
          </TableCell>
        </TableRow>
      </TableHead>
      <TableBody>
        {filteredAndSortedItems.map((item) => {
          const icon = item.current?.icon || '';
          const iconName = item.current?.name || item.name || item.key;
          const iconAlt = t('branding.iconsTab.iconAlt', {
            name: iconName,
          });
          const iconSrc = getIconSrc(icon, baseAssetUrl);

          return (
            <TableRow key={`${item.category}-${item.key}`}>
              <TableCell
                sx={{
                  height: { xs: 50, md: 60 },
                  p: { xs: 0.25, md: 0.5 },
                }}
              >
                {iconSrc ? (
                  <Box
                    component="span"
                    role="img"
                    aria-label={iconAlt}
                    sx={{
                      width: '100%',
                      height: { xs: 40, md: 50 },
                      m: 0.25,
                      display: 'block',
                    }}
                  >
                    <img
                      src={iconSrc}
                      alt=""
                      aria-hidden="true"
                      onError={(e) => {
                        (e.target as HTMLImageElement).style.display = 'none';
                      }}
                      style={{
                        width: '100%',
                        height: '100%',
                        objectFit: 'contain',
                        borderRadius: 4,
                        display: 'block',
                      }}
                    />
                  </Box>
                ) : (
                  <Avatar
                    sx={{
                      width: { xs: 40, md: 50 },
                      height: { xs: 40, md: 50 },
                      bgcolor: 'var(--color-greyish-blue)',
                      m: 0.25,
                    }}
                    role="img"
                    aria-label={iconAlt}
                  >
                    <Image
                      fontSize="small"
                      sx={{ color: 'var(--color-dark-greyish-blue)' }}
                      aria-hidden="true"
                    />
                  </Avatar>
                )}
              </TableCell>
              <TableCell align="center">
                <Button
                  variant="link"
                  component="label"
                  size="small"
                  role="button"
                  tabIndex={
                    impersonationLock || !item.editableFields.icon ? -1 : 0
                  }
                  startIcon={<CloudUpload aria-hidden />}
                  disabled={impersonationLock || !item.editableFields.icon}
                  aria-label={`Upload icon for ${item.name}`}
                  onKeyDown={(e) => {
                    if (
                      (e.key === 'Enter' || e.key === ' ') &&
                      !impersonationLock &&
                      item.editableFields.icon
                    ) {
                      e.preventDefault();
                      e.currentTarget.click();
                    }
                  }}
                  sx={{
                    '&.Mui-disabled': {
                      color: 'var(--color-deep-ocean-blue)',
                      opacity: 0.5,
                    },
                  }}
                >
                  {t('branding.iconsTab.uploadButton')}
                  <input
                    type="file"
                    accept=".png,.svg"
                    onChange={(e) => {
                      if (e.target.files && e.target.files[0]) {
                        onIconChange(
                          item.category,
                          item.key,
                          e.target.files[0]
                        );
                      }
                    }}
                    hidden
                    disabled={!item.editableFields.icon}
                    tabIndex={-1}
                    aria-hidden
                  />
                </Button>
              </TableCell>

              <TableCell align="center">
                {!item.current.icon?.includes('assets') &&
                  item.editableFields.icon && (
                    <Button
                      variant="link"
                      size="small"
                      startIcon={<RestartAlt aria-hidden />}
                      onClick={() => onResetIcon(item.category, item.key)}
                      disabled={
                        impersonationLock ||
                        resettingItems.has(`${item.category}-${item.key}`) ||
                        !item.editableFields.icon
                      }
                      title={t('branding.iconsTab.resetTooltip') as string}
                      aria-label={`Reset icon for ${item.name}`}
                    >
                      {t('branding.iconsTab.resetButton')}
                    </Button>
                  )}
              </TableCell>

              <TableCell align="left">
                {item.editableFields.name &&
                editingName === `${item.category}-${item.key}` ? (
                  <TextField
                    defaultValue={item.current.name || item.name}
                    onBlur={(e) =>
                      onFinishEditingName(
                        item.category,
                        item.key,
                        e.target.value
                      )
                    }
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') {
                        onFinishEditingName(
                          item.category,
                          item.key,
                          (e.target as HTMLInputElement).value
                        );
                      } else if (e.key === 'Escape') {
                        onCancelEditingName();
                      }
                    }}
                    variant="outlined"
                    size="small"
                    fullWidth
                    autoFocus
                    sx={{
                      '& .MuiInputBase-input': {
                        padding: { xs: '6px 8px', md: '8px 12px' },
                        fontSize: { xs: '0.75rem', md: '14px' },
                        textTransform: 'capitalize',
                      },
                    }}
                  />
                ) : (
                  <Typography
                    variant="body1"
                    sx={{
                      textTransform: 'capitalize',
                      fontWeight: 'medium',
                      cursor:
                        item.editableFields.name && !impersonationLock
                          ? 'pointer'
                          : 'default',
                      '&:hover':
                        item.editableFields.name && !impersonationLock
                          ? {
                              backgroundColor:
                                'var(--color-light-greyish-blue)',
                              borderRadius: 1,
                              padding: '2px 4px',
                              margin: '-2px -4px',
                            }
                          : {},
                    }}
                    onClick={
                      item.editableFields.name && !impersonationLock
                        ? () => onStartEditingName(item.category, item.key)
                        : undefined
                    }
                  >
                    {item.name}
                  </Typography>
                )}
              </TableCell>
              <TableCell>
                <Typography variant="body1" fontWeight="medium">
                  {item.type}
                </Typography>
              </TableCell>

              <TableCell sx={{ fontSize: { xs: '0.75rem', md: '0.875rem' } }}>
                {item.editableFields.description &&
                editingDescription === `${item.category}-${item.key}` ? (
                  <TextField
                    defaultValue={item.current.description}
                    placeholder={
                      t('branding.iconsTab.descriptionPlaceholder') as string
                    }
                    onBlur={(e) =>
                      onFinishEditing(item.category, item.key, e.target.value)
                    }
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') {
                        onFinishEditing(
                          item.category,
                          item.key,
                          (e.target as HTMLInputElement).value
                        );
                      } else if (e.key === 'Escape') {
                        onCancelEditing();
                      }
                    }}
                    size="small"
                    fullWidth
                    autoFocus
                    error={!!getIconFieldError(item.category, item.key)}
                    helperText={getIconFieldError(item.category, item.key)?.[0]}
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        '& fieldset': {
                          borderColor: getIconFieldError(
                            item.category,
                            item.key
                          )
                            ? 'var(--color-dark-greyish-blue)'
                            : 'var(--color-scifree-teal)',
                        },
                      },
                      '& .MuiInputBase-input': {
                        padding: { xs: '6px 8px', md: '8px 12px' },
                        fontSize: { xs: '0.75rem', md: '14px' },
                      },
                    }}
                  />
                ) : (
                  <Box
                    onClick={
                      item.editableFields.description && !impersonationLock
                        ? () => onStartEditing(item.category, item.key)
                        : undefined
                    }
                    onKeyDown={(e) => {
                      if (
                        item.editableFields.description &&
                        !impersonationLock &&
                        (e.key === 'Enter' || e.key === ' ')
                      ) {
                        e.preventDefault();
                        onStartEditing(item.category, item.key);
                      }
                    }}
                    tabIndex={
                      item.editableFields.description && !impersonationLock
                        ? 0
                        : -1
                    }
                    sx={{
                      minHeight: { xs: 32, md: 40 },
                      padding: { xs: '6px 8px', md: '8px 12px' },
                      fontSize: { xs: '0.75rem', md: '14px' },
                      cursor:
                        item.editableFields.description && !impersonationLock
                          ? 'pointer'
                          : 'default',
                      borderRadius: 1,
                      border: '1px solid var(--color-greyish-blue)',

                      '&:hover':
                        item.editableFields.description && !impersonationLock
                          ? {
                              backgroundColor: 'var(--color-greyish-blue)',
                              border: '1px solid',
                              borderColor: 'var(--color-dark-greyish-blue)',
                            }
                          : {},
                      '&:focus-visible':
                        item.editableFields.description && !impersonationLock
                          ? {
                              outline: '2px solid',
                              outlineColor: 'var(--color-scifree-teal)',
                              outlineOffset: '1px',
                            }
                          : {},
                      display: 'flex',
                      alignItems: 'center',
                    }}
                    role={
                      item.editableFields.description && !impersonationLock
                        ? 'button'
                        : 'text'
                    }
                    aria-label={
                      item.editableFields.description && !impersonationLock
                        ? `Edit description for ${item.name}`
                        : undefined
                    }
                  >
                    {item.current.description || (
                      <Typography
                        variant="body2"
                        sx={{
                          fontStyle: 'italic',
                        }}
                      >
                        {t('branding.iconsTab.descriptionPlaceholder')}
                      </Typography>
                    )}
                  </Box>
                )}
              </TableCell>
            </TableRow>
          );
        })}
      </TableBody>
    </Table>
  );
}
